import { createQueryKeys, inferQueryKeys } from '@lukemorales/query-key-factory';
import { AxiosRequestConfig, AxiosResponse } from 'axios';

import {
  $httpRootApi,
  UpdateCacheCallback,
  updateQueryCache,
  useMutation,
  UseMutationOptions,
  useQuery,
  UseQueryOptions,
} from '@core/http-client';
import { Company } from '@shared/models/company/company';
import { getQueriesAsSearch } from '@shared/utils/common';

import {
  CompaniesAcListPayload,
  CompaniesBulkDeletePayload,
  CompaniesBulkUpdateStatusPayload,
  CompaniesBulkUpdateTagsPayload,
  CompaniesBulkUpdateTypePayload,
  CompaniesCreateCommentPayload,
  CompaniesListPayload,
  CompaniesResponse,
  CompanyCreatePayload,
  CompanyShowPayload,
  CompanyUpdatePayload,
} from './companiesApi.types';

export const companyQueries = {
  show: async (payload: CompanyShowPayload, config?: AxiosRequestConfig) =>
    $httpRootApi.get<Company>(`/companies/${payload.id}${getQueriesAsSearch(payload.query)}`, config),
};

export const companiesQueryKeys = createQueryKeys('companies', {
  all: null,
  list: (payload: CompaniesListPayload) => ({
    queryKey: [payload.query],
    queryFn: async ({ signal }) =>
      (await $httpRootApi.get<CompaniesResponse>(`/companies${getQueriesAsSearch(payload.query)}`, { signal })).data,
  }),
  acList: (payload: CompaniesAcListPayload) => ({
    queryKey: [payload.query],
    queryFn: async ({ signal }) =>
      (await $httpRootApi.get<CompaniesResponse>(`/companies/ac${getQueriesAsSearch(payload.query)}`, { signal })).data,
  }),
  show: (payload: CompanyShowPayload) => ({
    queryKey: [payload.id, payload.query],
    queryFn: async ({ signal }) => (await companyQueries.show(payload, { signal })).data,
  }),
});

export type CompaniesQueryKeys = inferQueryKeys<typeof companiesQueryKeys>;

// -- Queries --

export const useCompaniesListQuery = (payload: CompaniesListPayload, options?: UseQueryOptions<CompaniesResponse>) =>
  useQuery<CompaniesQueryKeys['list']['queryKey'], CompaniesResponse>({
    ...companiesQueryKeys.list(payload),
    keepPreviousData: true,
    ...options,
  });

export const useCompanyShowQuery = (payload: CompanyShowPayload, options?: UseQueryOptions<Company>) =>
  useQuery<CompaniesQueryKeys['show']['queryKey'], Company>({
    ...companiesQueryKeys.show(payload),
    ...options,
  });

// -- Mutations --

export const useCompanyCreateMutation = (opts?: UseMutationOptions<CompanyCreatePayload, Company>) =>
  useMutation({
    ...opts,
    mutationFn: async (data) =>
      (await $httpRootApi.post<CompanyCreatePayload['body'], AxiosResponse<Company>>(`/companies`, data.body)).data,
  });

export const useCompanyUpdateMutation = (opts?: UseMutationOptions<CompanyUpdatePayload, Company>) =>
  useMutation({
    ...opts,
    mutationFn: async (data) =>
      (await $httpRootApi.put<CompanyUpdatePayload['body'], AxiosResponse<Company>>(`/companies/${data.id}`, data.body))
        .data,
  });

export const useCompaniesCreateCommentMutation = (opts?: UseMutationOptions<CompaniesCreateCommentPayload, Company>) =>
  useMutation({
    ...opts,
    mutationFn: async (data) =>
      (
        await $httpRootApi.post<CompaniesCreateCommentPayload['body'], AxiosResponse<Company>>(
          `/companies/${data.id}/comment`,
          data.body
        )
      ).data,
  });

// -- Bulk Mutations --

export const useCompaniesBulkUpdateStatusMutation = (
  opts?: UseMutationOptions<CompaniesBulkUpdateStatusPayload, any>
) =>
  useMutation({
    ...opts,
    mutationFn: async (data) =>
      (
        await $httpRootApi.put<CompaniesBulkUpdateStatusPayload['body'], AxiosResponse<any>>(
          `/companies/bulk-change-status`,
          data.body
        )
      ).data,
  });

export const useCompaniesBulkUpdateTypeMutation = (opts?: UseMutationOptions<CompaniesBulkUpdateTypePayload, any>) =>
  useMutation({
    ...opts,
    mutationFn: async (data) =>
      (
        await $httpRootApi.put<CompaniesBulkUpdateTypePayload['body'], AxiosResponse<any>>(
          `/companies/bulk-change-type`,
          data.body
        )
      ).data,
  });

export const useCompaniesBulkUpdateTagsMutation = (opts?: UseMutationOptions<CompaniesBulkUpdateTagsPayload, any>) =>
  useMutation({
    ...opts,
    mutationFn: async (data) =>
      (
        await $httpRootApi.put<CompaniesBulkUpdateTagsPayload['body'], AxiosResponse<any>>(
          `/companies/bulk-update-tags`,
          data.body
        )
      ).data,
  });

export const useCompaniesBulkDeleteMutation = (opts?: UseMutationOptions<CompaniesBulkDeletePayload, any>) =>
  useMutation({
    ...opts,
    mutationFn: async (data) =>
      (
        await $httpRootApi.delete<CompaniesBulkDeletePayload['body'], AxiosResponse<any>>(`/companies/bulk`, {
          data: data.body,
        })
      ).data,
  });

// ---- Utils ----

export const updateCompanyShowCache = (payload: CompanyShowPayload, callback: UpdateCacheCallback<Company>) =>
  updateQueryCache<Company>(companiesQueryKeys.show(payload).queryKey, callback);
